import { AuthAction } from '@store/interfaces';
import { add, getUnixTime } from 'date-fns';
import Cookies from 'universal-cookie';

import { REFRESH_TOKEN_KEY, TOKEN_KEY, TOKEN_THRESHOLD_KEY, USER_TYPE } from './constants';
import ENV from './env';

/**
 * Client-side access-token validity threshold (unix seconds).
 * Prefer explicit `expires_in` (access TTL seconds). Fall back to absolute epoch ms, then duration seconds.
 */
export const setThreshold = (time?: number, accessExpiresInSeconds?: number): string => {
	if (accessExpiresInSeconds != null && Number.isFinite(accessExpiresInSeconds) && accessExpiresInSeconds > 0) {
		return getUnixTime(add(new Date(), { seconds: Math.floor(accessExpiresInSeconds) })).toString();
	}

	const value = Number(time);
	if (!Number.isFinite(value) || value <= 0) {
		return getUnixTime(add(new Date(), { seconds: 3600 })).toString();
	}

	// Absolute epoch milliseconds (historical refresh expiry_time from backend).
	if (value > 1_000_000_000_000) {
		return Math.floor(value / 1000).toString();
	}

	// Absolute epoch seconds.
	if (value > 1_000_000_000) {
		return Math.floor(value).toString();
	}

	// Relative duration in seconds.
	return getUnixTime(add(new Date(), { seconds: value })).toString();
};

export const handleItem = (key: string, value?: string): void => {
	if (value) {
		localStorage.setItem(key, value);
	} else {
		localStorage.removeItem(key);
	}
};

/* eslint-disable @typescript-eslint/no-non-null-assertion */
// SECURITY NOTE: Tokens in localStorage are vulnerable to XSS. Refresh tokens should be
// HttpOnly, Secure, SameSite cookies set by the backend. Current implementation keeps
// localStorage for compatibility but adds Secure cookie mirroring. Migrate to HttpOnly
// backend cookies + short-lived access tokens (4h) as per SECURITY_AUDIT_REPORT.
export const setItems = (data: AuthAction['payload']): void => {
	const accessExpiresIn = Number(
		(data as any)?.expires_in ?? (data as any)?.expiresIn ?? 0
	);
	const expiryTime = Number(data!.expiry_time!);
	const threshold = setThreshold(expiryTime, accessExpiresIn > 0 ? accessExpiresIn : undefined);

	handleItem(TOKEN_KEY, data!.access_token);
	handleItem(TOKEN_THRESHOLD_KEY, threshold);
	handleItem(REFRESH_TOKEN_KEY, data!.refresh_token);
	handleItem(USER_TYPE, data!.user_type);
	const cookies = new Cookies('LOGIN', { domain: ENV.AUTH_BASE_DOMAIN });
	const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
	const cookieExpiry =
		expiryTime > 1_000_000_000_000
			? new Date(expiryTime)
			: expiryTime > 1_000_000_000
				? new Date(expiryTime * 1000)
				: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
	cookies.set(TOKEN_KEY, data!.access_token, {
		domain: ENV.AUTH_BASE_DOMAIN,
		expires: cookieExpiry,
		path: '/',
		secure: isSecure,
		sameSite: 'lax'
	});
	cookies.set(REFRESH_TOKEN_KEY, data!.refresh_token!, {
		domain: ENV.AUTH_BASE_DOMAIN,
		expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
		path: '/',
		secure: isSecure,
		sameSite: 'lax'
	});
	cookies.set(TOKEN_THRESHOLD_KEY, threshold, {
		domain: ENV.AUTH_BASE_DOMAIN,
		expires: cookieExpiry,
		path: '/',
		secure: isSecure,
		sameSite: 'lax'
	});
};
/* eslint-enable @typescript-eslint/no-non-null-assertion */

export const removeItems = (): void => {
	handleItem(TOKEN_KEY);
	handleItem(TOKEN_THRESHOLD_KEY);
	handleItem(REFRESH_TOKEN_KEY);

	const cookies = new Cookies('LOGIN', { domain: ENV.AUTH_BASE_DOMAIN });
	cookies.remove(TOKEN_KEY, { domain: ENV.AUTH_BASE_DOMAIN });
	cookies.remove(REFRESH_TOKEN_KEY, { domain: ENV.AUTH_BASE_DOMAIN });
	cookies.remove(TOKEN_THRESHOLD_KEY, { domain: ENV.AUTH_BASE_DOMAIN });
	cookies.remove(USER_TYPE, { domain: ENV.AUTH_BASE_DOMAIN });
};
